import React, { useState } from "react";
import { AlertTriangle, Download, X as XIcon, Package } from "lucide-react";

/**
 * BulkTooLargeModal — zeigt bei HTTP 413 die Unterkategorie-Vorschläge an.
 *
 * Props:
 *  - data: { error, size_mb, limit_mb, anzahl_files, kategorie, vorschlag,
 *            alternative_unterkategorien: [{key, label, size_mb, files}, ...] }
 *  - onClose: () => void
 *  - onPickSub: (sub) => void  — typischerweise navigiert zum Unter-Download
 */
export default function BulkTooLargeModal({ data, onClose, onPickSub }) {
  if (!data) return null;
  const subs = data.alternative_unterkategorien || [];
  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-navy/40 z-[80]" />
      <div
        data-testid="bulk-too-large-modal"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(640px,95vw)] max-h-[90vh] overflow-y-auto bg-white border border-navy/15 z-[90] shadow-xl"
      >
        <header className="border-b border-navy/10 px-6 py-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <AlertTriangle size={22} className="text-tot mt-1 shrink-0" />
            <div className="min-w-0">
              <div className="pb-eyebrow">Zu groß für ZIP-Download</div>
              <h3 className="font-display font-semibold text-navy text-lg sm:text-xl mt-1 tracking-tight break-words">
                {data.kategorie ? data.kategorie : "Komplett-Download"} — {data.size_mb} MB
              </h3>
              <p className="text-xs font-sans text-navy/65 mt-1 tabular-nums">
                {data.anzahl_files} Dateien · Limit {data.limit_mb} MB
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-navy/55 hover:text-navy p-1 shrink-0" aria-label="Schließen">
            <XIcon size={18} />
          </button>
        </header>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm font-sans text-navy/75">
            {data.vorschlag || `Diese Auswahl überschreitet das 500-MB-Limit. Bitte einzelne Unterordner herunterladen:`}
          </p>

          {subs.length > 0 ? (
            <div className="space-y-2" data-testid="bulk-subfolder-list">
              <div className="pb-eyebrow">Unterordner-Vorschlag</div>
              {subs.map((sub) => {
                const subClass = sub.size_mb <= data.limit_mb
                  ? "border-navy/15 hover:bg-ledger hover:border-navy/30"
                  : "border-tot/30 bg-tot/5";
                const disabled = sub.size_mb > data.limit_mb;
                return (
                  <button
                    key={sub.key}
                    onClick={() => !disabled && onPickSub?.(sub)}
                    disabled={disabled}
                    data-testid={`bulk-sub-${sub.label.replace(/\W+/g, "_")}`}
                    className={
                      "w-full text-left border p-3 transition-colors flex items-center gap-3 " + subClass +
                      (disabled ? " cursor-not-allowed" : " cursor-pointer")
                    }
                  >
                    <Package size={16} className={disabled ? "text-tot/60" : "text-gold"} />
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-medium text-navy text-sm tracking-tight break-words">{sub.label}</div>
                      <div className="text-[11px] font-sans text-navy/55 mt-0.5 tabular-nums">
                        {sub.files} Dateien · {sub.size_mb} MB
                        {disabled && <span className="text-tot ml-2 uppercase tracking-widish">— auch zu groß</span>}
                      </div>
                    </div>
                    {!disabled && <Download size={14} className="text-navy/55 shrink-0" />}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="border-l-[3px] border-l-tot bg-tot/5 px-4 py-3 text-xs font-sans text-navy">
              Diese Kategorie hat keine erkennbaren Unterordner. Bitte einzelne Dateien aus der Liste herunterladen.
            </div>
          )}

          <div className="text-[11px] font-sans text-navy/55 border-t border-navy/10 pt-3">
            Tipp: Cloudflare-Edge unterbricht ZIP-Downloads nach 60 Sekunden. Mehrere kleinere Pakete sind schneller und stabiler als ein großer.
          </div>
        </div>

        <div className="px-6 py-3 border-t border-navy/10 flex justify-end">
          <button onClick={onClose} className="pb-btn-ghost" data-testid="bulk-too-large-close">Schließen</button>
        </div>
      </div>
    </>
  );
}

/**
 * Helper-Hook für Komponenten, die einen ZIP-Download mit 413-Handling brauchen.
 * Verwendung:
 *   const { triggerZip, modal } = useBulkZip();
 *   <button onClick={() => triggerZip(url)}>ZIP</button>
 *   {modal}
 */
export function useBulkZip() {
  const [tooLarge, setTooLarge] = useState(null);
  const [zipBaseUrl, setZipBaseUrl] = useState(null);

  const triggerZip = async (url) => {
    setZipBaseUrl(url);
    try {
      // Pre-Flight: HEAD/GET-Status checken, ohne sofort den Stream zu starten.
      // Wir machen einen GET mit Range "0-0" um den Status zu lernen. Wenn 413,
      // dann lesen wir die JSON-Body. Wenn nicht-413, dann direkt Browser-DL.
      const resp = await fetch(url, { method: "GET" });
      if (resp.status === 413) {
        const data = await resp.json();
        setTooLarge(data);
        return;
      }
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: resp.statusText }));
        alert(`Fehler beim ZIP-Download: ${err.detail || resp.statusText}`);
        return;
      }
      // 200 OK → Blob → Browser-Save-As
      const blob = await resp.blob();
      const cd = resp.headers.get("content-disposition") || "";
      const fnMatch = cd.match(/filename="([^"]+)"/);
      const filename = fnMatch ? fnMatch[1] : "datenraum.zip";
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
    } catch (e) {
      alert(`ZIP-Download fehlgeschlagen: ${e}`);
    }
  };

  const handlePickSub = (sub) => {
    // sub.key = "06_Mietverträge/06.4_OE Newton" → in subfolder-Param packen
    // Original-Datenraum-URL muss `?subfolder=<label>` ergänzt bekommen
    if (!zipBaseUrl) return;
    const join = zipBaseUrl.includes("?") ? "&" : "?";
    const url = `${zipBaseUrl}${join}subfolder=${encodeURIComponent(sub.label)}`;
    setTooLarge(null);
    triggerZip(url);
  };

  const modal = tooLarge ? (
    <BulkTooLargeModal
      data={tooLarge}
      onClose={() => setTooLarge(null)}
      onPickSub={handlePickSub}
    />
  ) : null;

  return { triggerZip, modal };
}
